import { Divider, Table, Thead, Tbody, Heading } from '@chakra-ui/react';
import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { ScrollSync, ScrollSyncPane } from 'react-scroll-sync';

import { EmptyTableRow } from 'components/Table';
import EmployeeGroupTableBody from 'containers/Reports/EmployeeReports/EmployeeGroupTableBody';
import EmployeeTableBody from 'containers/Reports/EmployeeReports/EmployeeTableBody';
import ExportMenu from 'containers/Reports/shared/ExportMenu';
import { ReportsTableHeadCell } from 'containers/Reports/shared/ReportsTableCell';
import ReportsTableRow from 'containers/Reports/shared/ReportsTableRow';
import useStickyHeader from 'containers/Reports/shared/useStickyHeader';
import { roles } from 'global/rolesAndPermissions';
import { useUserObject } from 'store/useUserStore';

interface IEmployeeReportsTableProps {
  data: any;
  checkedTimesheet: string[];
  groupId: any;
  userId?: any;
  dateRange: Array<any>;
  isLoading?: boolean;
  handleCheckboxChange: (e: React.ChangeEvent<HTMLInputElement>, timeSheetId: string) => void;
  children?: React.ReactNode | undefined;
}

const EmployeeReportsTable: React.FC<IEmployeeReportsTableProps> = ({
  children,
  data,
  groupId,
  userId,
  dateRange,
  checkedTimesheet,
  isLoading,
  handleCheckboxChange,
}) => {
  const { t } = useTranslation(['common', 'reports']);

  const userStore = useUserObject();
  useStickyHeader();

  const getTimesheetIds = useCallback(() => {
    let timesheetIds = [];

    if (Array.isArray(data?.users) && data.users.length) {
      timesheetIds = data?.users
        ?.map((user: any) => {
          return Array.isArray(user?.data?.timesheets) && user.data.timesheets.length
            ? user?.data?.timesheets?.map((timesheet: any) => timesheet?.id)
            : [];
        })
        .flat();
    }

    if (Array.isArray(data?.timesheets) && data.timesheets.length) {
      timesheetIds = data?.timesheets.map((timesheet: any) => timesheet?.id);
    }

    return timesheetIds;
  }, [data]);

  return (
    <div>
      <Divider my="4" />
      <Heading variant="h2" size="md" fontWeight="semibold" mb="4">
        {t('reports:searchResults')}
      </Heading>
      <ExportMenu
        attributes={data?.attributes}
        dateRange={dateRange}
        userId={userId}
        groupId={groupId}
        reportType="byEmployee"
        timesheetIds={getTimesheetIds()}
        displaySettings={data?.integrations}
      />
      <ScrollSync>
        <div style={{ position: 'relative' }}>
          <Table display="block" id="js-report-table">
            <ScrollSyncPane group="horizontal">
              <Thead
                display="block"
                position="sticky"
                top="0"
                background="primary.50"
                max-width="100%"
                overflow="auto"
                sx={{
                  '&::-webkit-scrollbar': { display: 'none' },
                  '-ms-overflow-style': 'none',
                  scrollbarWidth: 'none',
                }}
              >
                <ReportsTableRow color="gray.600" mb="0" border="0">
                  <ReportsTableHeadCell width="30px" justifyContent="center" flex="30 0 auto"></ReportsTableHeadCell>
                  <ReportsTableHeadCell>{t('common:name')}</ReportsTableHeadCell>
                  {userStore?.companyInfo?.show_employee_id ? (
                    <ReportsTableHeadCell width="120px">{t('common:userID')}</ReportsTableHeadCell>
                  ) : null}
                  <ReportsTableHeadCell width="250px">{t('common:jobOrTask')}</ReportsTableHeadCell>
                  <ReportsTableHeadCell width="200px">{t('common:clockIn/Out')}</ReportsTableHeadCell>
                  <ReportsTableHeadCell width="150px">{`${t('common:paidBreaks')} (${t(
                    'common:mins'
                  )})`}</ReportsTableHeadCell>
                  <ReportsTableHeadCell width="150px">{`${t('common:unpaidBreaks')} (${t(
                    'common:mins'
                  )})`}</ReportsTableHeadCell>
                  <ReportsTableHeadCell width="120px">{t('common:duration')}</ReportsTableHeadCell>
                  {userStore?.companyInfo?.show_mileage ? (
                    <ReportsTableHeadCell width="140px">{t('common:mph/km')}</ReportsTableHeadCell>
                  ) : null}
                  {userStore?.companyInfo?.weekly_overtime ? (
                    <>
                      <ReportsTableHeadCell width="120px">{t('reports:regularHrs')}</ReportsTableHeadCell>
                      <ReportsTableHeadCell width="120px">{t('reports:regularPay')}</ReportsTableHeadCell>
                    </>
                  ) : null}
                  {userStore?.companyInfo?.daily_overtime ? (
                    <ReportsTableHeadCell width="120px">{t('reports:OTHrs')}</ReportsTableHeadCell>
                  ) : null}
                  {userStore?.companyInfo?.daily_double_overtime ? (
                    <ReportsTableHeadCell width="120px">{t('reports:DOThrs')}</ReportsTableHeadCell>
                  ) : null}
                  {userStore?.companyInfo?.show_timesheet_notes && (
                    <ReportsTableHeadCell>{t('common:notes')}</ReportsTableHeadCell>
                  )}
                  {userStore?.userInfo?.roleId === roles.ADMIN || userStore?.permissions?.manageTimesheets ? (
                    <ReportsTableHeadCell width="80px"></ReportsTableHeadCell>
                  ) : null}
                </ReportsTableRow>
              </Thead>
            </ScrollSyncPane>
            <ScrollSyncPane group="horizontal">
              <Tbody display="block" max-width="100%" overflow="auto" mt={2}>
                {Array.isArray(data?.timesheets) && data.timesheets.length ? (
                  <EmployeeTableBody
                    data={data}
                    checkedTimesheet={checkedTimesheet}
                    handleCheckboxChange={handleCheckboxChange}
                  />
                ) : userId && !isLoading ? (
                  <EmptyTableRow />
                ) : null}
                {Array.isArray(data?.users) && data.users.length ? (
                  <EmployeeGroupTableBody
                    data={data}
                    checkedTimesheet={checkedTimesheet}
                    handleCheckboxChange={handleCheckboxChange}
                  />
                ) : groupId && !isLoading ? (
                  <EmptyTableRow />
                ) : null}
              </Tbody>
            </ScrollSyncPane>
          </Table>
        </div>
      </ScrollSync>
      {children}
    </div>
  );
};
export default EmployeeReportsTable;
